import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { acceptSupply } from '../../api/store/acceptSupply';
import { Supply } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormDatePicker from '../../components/forms/FormDatePicker';
import FormSelect from '../../components/forms/FormSelect';
import FormSwitch from '../../components/forms/FormSwitch';
import Image from '../../components/Image';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import currency from '../../utils/currency';
import { getPriceByUnit } from '../../utils/getPriceByUnit';
import getQntLabel from '../../utils/getQntLabel';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  supply?: DbDocument<Supply> | null;
}

type FormType = {
  storeId: string;
  time: number;
  shouldAddTransaction: boolean;
  walletId: string;
};

const SupplyAcceptModal = (props: Props) => {
  const { onClose, supply, onSubmit } = props;
  const { stores, storesMap, worker, productsMap, ingredientsMap, wallets, walletsMap } = useCafe();
  const { cafeSlug, cafe } = useAdmin();
  const { lang } = useLocale();

  const form = useForm<FormType>({ defaultValues: { time: Date.now(), shouldAddTransaction: false } });
  const storeId = form.watch('storeId');

  const shouldAddTransaction = form.watch('shouldAddTransaction');
  const totalCost = useMemo(
    () =>
      supply?.components.reduce(
        (acc, { id, qnt, price }) => acc + getPriceByUnit(price || 0, productsMap[id]) * qnt,
        0
      ) || 0,
    [productsMap, supply?.components]
  );

  const handleSubmit = useCallback(
    async (values: FormType) => {
      const store = storesMap[values.storeId];

      if (!supply || !worker || !store) {
        return;
      }

      const { shouldAddTransaction, time } = values;
      const wallet = walletsMap[values.walletId];

      await acceptSupply({
        cafeSlug,
        supply,
        worker,
        store,
        wallet,
        shouldAddTransaction,
        productsMap,
        totalCost,
        time,
      });
      onSubmit();
      onClose();
    },
    [storesMap, supply, worker, walletsMap, cafeSlug, productsMap, onClose, totalCost, onSubmit]
  );

  return (
    <EditModal
      open
      title={`${t.accept_supply[lang]} №${supply?.number}`}
      form={form}
      onClose={onClose}
      submitButton={t.accept_supply[lang]}
      submitButtonProps={{ disabled: !storeId }}
      onSubmit={handleSubmit}
      size="sm"
    >
      <Box sx={{ maxWidth: 300 }}>
        <FormDatePicker name="time" label={t.date[lang]} disableFuture sx={{ mb: 2 }} />

        <FormSelect
          name="storeId"
          color="secondary"
          label={t.store[lang]}
          options={stores.map((s) => s.id)}
          renderOption={(o) => storesMap[o]?.name}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <Accordion elevation={0} sx={{ border: (t) => `1px solid ${t.palette.grey[400]}`, '::before': { opacity: 0 } }}>
          <AccordionSummary expandIcon={<ExpandMore />}>{t.products[lang]}</AccordionSummary>
          <AccordionDetails>
            {supply?.components.map((component) => (
              <Grid
                key={component.id}
                container
                alignItems="center"
                mb={{ xs: 2, md: 0 }}
                sx={{ p: 0.5, borderRadius: 1, ':hover': { bgcolor: (t) => t.palette.background.default } }}
              >
                <Grid item xs={10} sm={4.5} sx={{ pr: { xs: 0, sm: 1 } }}>
                  <Box gap={1} sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <Image src={productsMap[component.id]?.imageUrl} />
                    <Box>
                      <Typography sx={{ lineHeight: 0.8 }}>{productsMap[component.id]?.name}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={2.5} sx={{ pr: { xs: 0, sm: 1 } }}>
                  <Typography>{getQntLabel(component.qnt, ingredientsMap[component.id], lang)}</Typography>
                </Grid>

                <Grid item xs={12} sm={2.5} sx={{ pr: { xs: 0, sm: 1 } }}>
                  <Typography>x {currency(component.price || 0)}</Typography>
                </Grid>
                <Grid item xs={12} sm={2.5} sx={{ pr: { xs: 0, sm: 1 } }}>
                  <Typography>
                    {currency(getPriceByUnit(component.price || 0, ingredientsMap[component.id]) * component.qnt)}
                  </Typography>
                </Grid>
              </Grid>
            ))}

            <Typography variant="h6" textAlign="right">
              {t.total[lang]}: {currency(totalCost, cafe?.currency)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>

      <FormSwitch
        name="shouldAddTransaction"
        label={`${t.deduct_product_cost_from_bill[lang]} (${currency(totalCost, cafe?.currency)})`}
        checkbox
      />

      {shouldAddTransaction && (
        <Box sx={{ maxWidth: 300 }}>
          <FormSelect
            name="walletId"
            label={t.deduct_from_wallet[lang]}
            options={wallets.map((w) => w.id)}
            renderOption={(o) => walletsMap[o]?.name || ''}
            required
            rules={{ required: true }}
            sx={{ mt: 1 }}
          />
        </Box>
      )}
    </EditModal>
  );
};

export default SupplyAcceptModal;
